import currency from "currency.js";
import { utils } from "ethers";
import { CollateralAssetResponse } from "../codegen-api";
import { AllCollaterals } from "../constants/addresses";
import { dec } from "./amount/decimal";
import { floorToDecimalsString, normalizeDecimalString } from "./decimalString";

export const getRoundedValue = (
  value: string | number,
  precision: number = 2
): number => currency(value, { precision }).value;

/** Decimal places a step needs: 0.00000001 -> 8, 0.01 -> 2, 1 -> 0. */
export const stepDecimals = (stepSize: number | string): number => {
  const s = String(stepSize);
  const [mantissa, exponent] = s.split(/e-/i);
  const mantissaDecimals = mantissa.split(".")[1]?.length ?? 0;
  return exponent
    ? Number(exponent) + mantissaDecimals
    : mantissaDecimals;
};

/**
 * Decimals to render a step-aligned value with. A caller's `precision` must
 * never be COARSER than the step: ETH-USDC has `amount_step` 1e-8 but
 * `amount_precision` 4, and clipping to 4dp discards seven eighths of the
 * granularity the market allows.
 */
export const effectiveStepPrecision = (
  stepSize: number | string,
  precision: number = 2
): number => Math.min(20, Math.max(precision, stepDecimals(stepSize)));

/**
 * `value` snapped onto the step grid, as text at `decimals` places, or `null`
 * when either is not a number.
 *
 * Exact decimal arithmetic (`src/utils/amount/decimal.ts`), never float
 * multiplication: `662.21352206 * 1e8` lands just below the integer, which used
 * to cost a whole step on about 1 in 20 large sizes. A JS number is read at the
 * precision a double actually carries, so `1.15 - 0.05` reads as 1.1 rather than
 * 1.0999999999999999.
 */
const alignedToStep = (
  value: number | string,
  stepSize: number | string,
  decimals: number,
  mode: "floor" | "ceil"
): string | null => {
  const step = dec.parse(stepSize);
  if (step === null) return null;
  const snapped = dec.toStep(value, step, mode);
  return snapped === null ? null : dec.fixed(snapped, decimals, mode);
};

/**
 * The largest step-aligned value that does NOT exceed `value`.
 *
 * It floors at the display precision too. `toFixed` rounds half-UP, so a buy of
 * 2.6 USDC at 2475.19 (0.00105044 ETH) was written out as 0.0011 ETH and spent
 * $2.72 — 4.7% more than the trader entered. Rounding is never allowed to
 * increase what an order costs or sells.
 */
export const roundToStepSize = (
  value: number | string,
  stepSize: number,
  precision: number = 2
) => {
  const stepSizeNumber = Number(stepSize);
  if (Number.isNaN(Number(value)) || !(stepSizeNumber > 0)) {
    return 0;
  }

  const decimals = effectiveStepPrecision(stepSizeNumber, precision);
  const aligned = alignedToStep(value, stepSizeNumber, decimals, "floor");
  // Below one step (and negative) is 0, so callers can block the order.
  if (aligned === null || !dec.gt(aligned, "0")) {
    return 0;
  }
  return parseFloat(aligned);
};

/**
 * A step-aligned value as a string, at the step's own precision — so writing it
 * into a form field cannot round it back up (see `roundToStepSize`).
 */
export const toStepFixed = (
  value: number,
  stepSize: number,
  precision: number = 2
): string => value.toFixed(effectiveStepPrecision(stepSize, precision));

/**
 * `roundToStepSize` written out at the step's own precision: the one call for a
 * size or amount that goes into a field or an order. 0.00000148 on a 1e-8 step
 * with precision 6 or 7 stays "0.00000148" (`toFixed(7)` made it "0.0000015").
 */
export const floorToStepString = (
  value: number | string,
  stepSize: number,
  precision: number = 2
): string => {
  const decimals = effectiveStepPrecision(stepSize, precision);
  const aligned =
    Number(stepSize) > 0
      ? alignedToStep(value, Number(stepSize), decimals, "floor")
      : null;
  // Written straight from the exact result: going back through a JS number and
  // `toFixed` could blur the last digit of a 16-digit size.
  return aligned !== null && dec.gt(aligned, "0")
    ? aligned
    : dec.fixed("0", decimals, "floor");
};

/**
 * A size or balance a trader may copy back into a field: floored, at the step's
 * full precision, with trailing zeros trimmed down to `precision` places.
 * BTC 0.00000148 (step 1e-8, precision 6) shows "0.00000148"; 1.5 shows "1.500000".
 */
export const floorSizeForDisplay = (
  value: number | string | null | undefined,
  stepSize: number,
  precision: number = 2,
  /** Shown when `value` is not a number; defaults to a floored zero. */
  invalidText?: string
): string => {
  if (invalidText !== undefined && normalizeDecimalString(value) === null) {
    return invalidText;
  }
  const full = floorToDecimalsString(
    value,
    effectiveStepPrecision(stepSize, precision)
  );
  const [whole, fraction = ""] = full.split(".");
  const kept = fraction
    .replace(/0+$/, "")
    .padEnd(Math.min(precision, fraction.length), "0");
  return kept ? `${whole}.${kept}` : whole;
};

/**
 * A price aligned to the market's `price_step`, as plain decimal text.
 *
 * `direction` picks the side that is safe for the order: "down" for a buy (never
 * pays more than entered), "up" for a sell (never sells for less). Invalid input
 * gives "" so an empty field stays empty.
 */
export const alignPriceToStep = (
  value: number | string,
  stepSize: number,
  precision: number = 2,
  direction: "down" | "up" = "down"
): string => {
  if (value === "" || value === null || value === undefined) return "";
  const stepSizeNumber = Number(stepSize);
  if (!Number.isFinite(Number(value)) || !(stepSizeNumber > 0)) return "";

  const decimals = effectiveStepPrecision(stepSizeNumber, precision);
  const aligned = alignedToStep(
    value,
    stepSizeNumber,
    decimals,
    direction === "up" ? "ceil" : "floor"
  );
  return aligned ?? "";
};

/** Rounded UP to `precision` places — exact, so 0.01 × 57 stays 0.57. */
export const roundUpToPrecision = (value: number, precision: number = 2) =>
  parseFloat(dec.round(value, precision, "ceil"));

export const getValidTotalValue = (
  price: number,
  totalValue: number,
  contractsStepSize: number,
  contractsPrecision?: number
) => {
  const contractSize = totalValue / price;
  const validContractSize = roundToStepSize(
    contractSize,
    contractsStepSize,
    contractsPrecision
  );
  const validTotalValue = Number(validContractSize * price).toFixed(2);
  return Number(validTotalValue);
};

/**
 *
 * @param n Number
 * @param alwaysShowDecimals Optional.
 * If true, always show decimals. If false, clip decimals if required.
 * eg. with n of 10.1000, value of false will return 10.1.
 * @returns
 */
export const formatAmount = (
  n: number,
  alwaysShowDecimals?: boolean
): string => {
  if (n < 1e3) {
    return `${currency(n, { separator: ",", symbol: "" }).format()}`;
  }
  if (n >= 1e3 && n < 1e6) {
    const v = alwaysShowDecimals
      ? (n / 1e3).toFixed(2)
      : parseFloat((n / 1e3).toFixed(2));
    return `${v}K`;
  }
  if (n >= 1e6 && n < 1e9) {
    const v = alwaysShowDecimals
      ? (n / 1e6).toFixed(2)
      : parseFloat((n / 1e6).toFixed(2));
    return `${v}M`;
  }
  if (n >= 1e9 && n < 1e12) {
    const v = alwaysShowDecimals
      ? (n / 1e9).toFixed(2)
      : parseFloat((n / 1e9).toFixed(2));
    return `${v}B`;
  }
  if (n >= 1e12) {
    const v = alwaysShowDecimals
      ? (n / 1e12).toFixed(2)
      : parseFloat((n / 1e12).toFixed(2));
    return `${v}T`;
  }

  return "";
};

export const formatCollateralDecimals = (
  amount: string | number,
  collateral?: AllCollaterals
) => {
  if (collateral === CollateralAssetResponse.Weth) {
    return currency(amount, { precision: 4 }).format({ symbol: " " });
  }
  return currency(amount, { precision: 2 }).format({ symbol: " " });
};

/**
 * Convert wei value (18 decimals) to human-readable format
 * @param weiValue - Value in wei (as string or number)
 * @param decimals - Number of decimals (default 18)
 * @returns Number in human-readable format
 */
export const fromWei = (weiValue: string | number, decimals: number = 18): number => {
  if (!weiValue || weiValue === '0') return 0;
  try {
    return Number(utils.formatUnits(weiValue.toString(), decimals));
  } catch (error) {
    console.error('Error converting from wei:', error, weiValue);
    return 0;
  }
};
